import React, { useEffect, useRef, useState } from "react";

const VERTEX_SHADER = `
attribute vec3 aPosition;
attribute vec2 aTexCoord;
attribute float aShade;
attribute float aPattern;

uniform float uAspect;
uniform float uRotationX;
uniform float uRotationY;
uniform float uRotationZ;

varying vec2 vTexCoord;
varying float vShade;
varying float vPattern;

vec3 rotateX(vec3 point, float angle) {
  float s = sin(angle);
  float c = cos(angle);
  return vec3(point.x, point.y * c - point.z * s, point.y * s + point.z * c);
}

vec3 rotateY(vec3 point, float angle) {
  float s = sin(angle);
  float c = cos(angle);
  return vec3(point.x * c + point.z * s, point.y, -point.x * s + point.z * c);
}

vec3 rotateZ(vec3 point, float angle) {
  float s = sin(angle);
  float c = cos(angle);
  return vec3(point.x * c - point.y * s, point.x * s + point.y * c, point.z);
}

void main() {
  vec3 position = rotateZ(rotateY(rotateX(aPosition, uRotationX), uRotationY), uRotationZ);
  position.z -= 4.1;

  float focalLength = 2.5;
  gl_Position = vec4(
    position.x * focalLength / uAspect,
    position.y * focalLength,
    -position.z - 1.0,
    -position.z
  );

  vTexCoord = aTexCoord;
  vShade = aShade;
  vPattern = aPattern;
}
`;

const FRAGMENT_SHADER = `
precision mediump float;

uniform sampler2D uTexture;

varying vec2 vTexCoord;
varying float vShade;
varying float vPattern;

void main() {
  vec2 patternCoord = (vTexCoord - 0.5) * vec2(0.58, 0.72) + 0.5;
  vec4 pattern = texture2D(uTexture, patternCoord);
  float edgeDistance = min(
    min(vTexCoord.x, 1.0 - vTexCoord.x),
    min(vTexCoord.y, 1.0 - vTexCoord.y)
  );
  float edge = 1.0 - smoothstep(0.0, 0.035, edgeDistance);
  vec3 face = mix(vec3(0.09, 0.09, 0.09), vec3(0.18, 0.18, 0.18), vShade);
  vec3 color = mix(face, pattern.rgb, pattern.a * vPattern);

  color = mix(color, vec3(0.28, 0.28, 0.28), edge * 0.46);

  gl_FragColor = vec4(color * vShade, 1.0);
}
`;

const createShader = (gl, type, source) => {
  const shader = gl.createShader(type);

  gl.shaderSource(shader, source);
  gl.compileShader(shader);

  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }

  return shader;
};

const createProgram = (gl) => {
  const vertexShader = createShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
  const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);

  if (!vertexShader || !fragmentShader) {
    return null;
  }

  const program = gl.createProgram();
  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);

  gl.deleteShader(vertexShader);
  gl.deleteShader(fragmentShader);

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    gl.deleteProgram(program);
    return null;
  }

  return program;
};

const createLogoGeometry = () => {
  const size = 0.68;
  const x = size;
  const y = size;
  const z = size;
  const vertices = [];

  const pushVertex = (position, texCoord, shade, pattern) => {
    vertices.push(
      position[0],
      position[1],
      position[2],
      texCoord[0],
      texCoord[1],
      shade,
      pattern
    );
  };

  const pushFace = (corners, shade, pattern = 1) => {
    const texCoords = [
      [0, 1],
      [1, 1],
      [1, 0],
      [0, 0],
    ];

    [0, 1, 2, 0, 2, 3].forEach((index) => {
      pushVertex(corners[index], texCoords[index], shade, pattern);
    });
  };

  pushFace(
    [
      [-x, -y, z],
      [x, -y, z],
      [x, y, z],
      [-x, y, z],
    ],
    1,
    1
  );
  pushFace(
    [
      [x, -y, -z],
      [-x, -y, -z],
      [-x, y, -z],
      [x, y, -z],
    ],
    0.44,
    1
  );
  pushFace(
    [
      [x, -y, z],
      [x, -y, -z],
      [x, y, -z],
      [x, y, z],
    ],
    0.66,
    1
  );
  pushFace(
    [
      [-x, -y, -z],
      [-x, -y, z],
      [-x, y, z],
      [-x, y, -z],
    ],
    0.58,
    1
  );
  pushFace(
    [
      [-x, y, z],
      [x, y, z],
      [x, y, -z],
      [-x, y, -z],
    ],
    0.82,
    0
  );
  pushFace(
    [
      [-x, -y, -z],
      [x, -y, -z],
      [x, -y, z],
      [-x, -y, z],
    ],
    0.5,
    0
  );

  return new Float32Array(vertices);
};

const resizeCanvas = (canvas, gl) => {
  const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
  const width = Math.max(1, Math.round(canvas.clientWidth * pixelRatio));
  const height = Math.max(1, Math.round(canvas.clientHeight * pixelRatio));

  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width;
    canvas.height = height;
    gl.viewport(0, 0, width, height);
  }

  return width / height;
};

const WebGLCubeLogo = ({ textureSrc = "/images/dot-grid.svg" }) => {
  const canvasRef = useRef(null);
  const [showFallback, setShowFallback] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const gl =
      canvas &&
      (canvas.getContext("webgl", {
        alpha: true,
        antialias: true,
        premultipliedAlpha: false,
      }) ||
        canvas.getContext("experimental-webgl", {
          alpha: true,
          antialias: true,
          premultipliedAlpha: false,
        }));

    if (!gl) {
      setShowFallback(true);
      return undefined;
    }

    const program = createProgram(gl);

    if (!program) {
      setShowFallback(true);
      return undefined;
    }

    const geometry = createLogoGeometry();
    const vertexBuffer = gl.createBuffer();
    const texture = gl.createTexture();
    const stride = 7 * Float32Array.BYTES_PER_ELEMENT;
    const positionLocation = gl.getAttribLocation(program, "aPosition");
    const texCoordLocation = gl.getAttribLocation(program, "aTexCoord");
    const shadeLocation = gl.getAttribLocation(program, "aShade");
    const patternLocation = gl.getAttribLocation(program, "aPattern");
    const aspectLocation = gl.getUniformLocation(program, "uAspect");
    const rotationXLocation = gl.getUniformLocation(program, "uRotationX");
    const rotationYLocation = gl.getUniformLocation(program, "uRotationY");
    const rotationZLocation = gl.getUniformLocation(program, "uRotationZ");
    const textureLocation = gl.getUniformLocation(program, "uTexture");
    const reducedMotionQuery = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    );
    const image = new Image();
    let animationFrame = null;
    let destroyed = false;

    gl.useProgram(program);
    gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, geometry, gl.STATIC_DRAW);

    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 3, gl.FLOAT, false, stride, 0);
    gl.enableVertexAttribArray(texCoordLocation);
    gl.vertexAttribPointer(
      texCoordLocation,
      2,
      gl.FLOAT,
      false,
      stride,
      3 * Float32Array.BYTES_PER_ELEMENT
    );
    gl.enableVertexAttribArray(shadeLocation);
    gl.vertexAttribPointer(
      shadeLocation,
      1,
      gl.FLOAT,
      false,
      stride,
      5 * Float32Array.BYTES_PER_ELEMENT
    );
    gl.enableVertexAttribArray(patternLocation);
    gl.vertexAttribPointer(
      patternLocation,
      1,
      gl.FLOAT,
      false,
      stride,
      6 * Float32Array.BYTES_PER_ELEMENT
    );

    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.uniform1i(textureLocation, 0);
    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LEQUAL);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.clearColor(0, 0, 0, 0);

    const render = (time = 0) => {
      if (destroyed) {
        return;
      }

      const seconds = time * 0.001;
      const shouldReduceMotion = reducedMotionQuery.matches;
      const aspect = resizeCanvas(canvas, gl);
      const rotationY = shouldReduceMotion ? -0.4 : seconds * 0.86 - 0.42;
      const rotationX = shouldReduceMotion
        ? -0.2
        : -0.2 + Math.sin(seconds * 0.7) * 0.12;
      const rotationZ = shouldReduceMotion
        ? -0.03
        : -0.03 + Math.sin(seconds * 0.45) * 0.05;

      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      gl.uniform1f(aspectLocation, aspect);
      gl.uniform1f(rotationXLocation, rotationX);
      gl.uniform1f(rotationYLocation, rotationY);
      gl.uniform1f(rotationZLocation, rotationZ);
      gl.drawArrays(gl.TRIANGLES, 0, geometry.length / 7);

      if (!shouldReduceMotion) {
        animationFrame = window.requestAnimationFrame(render);
      }
    };

    image.onload = () => {
      if (destroyed) {
        return;
      }

      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
      gl.texImage2D(
        gl.TEXTURE_2D,
        0,
        gl.RGBA,
        gl.RGBA,
        gl.UNSIGNED_BYTE,
        image
      );
      setShowFallback(false);
      render();
    };

    image.onerror = () => setShowFallback(true);
    image.src = textureSrc;

    return () => {
      destroyed = true;
      image.onload = null;
      image.onerror = null;

      if (animationFrame) {
        window.cancelAnimationFrame(animationFrame);
      }

      gl.deleteTexture(texture);
      gl.deleteBuffer(vertexBuffer);
      gl.deleteProgram(program);
    };
  }, [textureSrc]);

  return (
    <span className="blockset-logo-scene" aria-hidden="true">
      <canvas className="blockset-logo-canvas" ref={canvasRef} />
      {showFallback && (
        <img src={textureSrc} alt="" className="blockset-logo-fallback" />
      )}
    </span>
  );
};

export default WebGLCubeLogo;
